import type { ReactNode } from "react";
import { styled } from "../../../../lib/styled";

/** One labelled value of a list of facts. */
export interface Fact {
  label: ReactNode;
  value: ReactNode;
}

// The label and the value are the two cells of one row of the list's grid.
const Pair = styled("div")({ display: "contents" });

const List = styled("dl")(({ theme }) => ({
  margin: 0,
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  alignItems: "center",
  rowGap: theme.spacing(0.5),
  columnGap: theme.spacing(2),
}));

const Label = styled("dt")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

const Value = styled("dd")<{ $align: "left" | "right" }>(({ theme, $align }) => ({
  ...theme.typography.body2,
  margin: 0,
  textAlign: $align,
}));

interface FactsProps {
  items: Fact[];
  /** Which side of its column a value sits on: "right", against the edge, for a short list of
   *  figures; "left", under one another like a table, for a list read line by line. Default "right". */
  valueAlign?: "left" | "right";
}

/** Labelled values in two columns, the label on the left and its value beside it. */
export function Facts({ items, valueAlign = "right" }: FactsProps) {
  return (
    <List>
      {items.map((item, position) => (
        <Pair key={position}>
          <Label>{item.label}</Label>
          <Value $align={valueAlign}>{item.value}</Value>
        </Pair>
      ))}
    </List>
  );
}
